import React, { useState } from 'react';

import {
  type SellableStudioPlanId,
  type StudioBillingInterval,
  toPlanChangeSelection,
} from 'src/routes/settings/plans/components/studio/studioPlanFeatures';
import { useStudioPlanChange } from 'src/routes/settings/plans/components/studio/useStudioPlanChange';
import {
  type StudioPlanId,
  useGetExtraAppsPreviewQuery,
  useGetStudioPlansQuery,
  usePurchaseExtraAppMutation,
} from 'src/services/api/studioCreditApi';

import {
  type AppLimitRoute,
  type AppLimitSurface,
  DEFAULT_UPGRADE_PLAN,
  getUpgradePlanOptions,
} from './appLimit';
import type { AppLimitApp } from './AppLimitAppCard';
import { AppLimitModal } from './AppLimitModal';
import { AppLimitUpgradeModal } from './AppLimitUpgradeModal';

interface AppLimitGateProps {
  onClose: () => void;
  /**
   * Resumes what the paywall interrupted — the build, or the pending app add.
   * Called only once the way past the limit has actually been bought: the
   * per-app fee charged, or the upgrade applied.
   */
  onProceed: () => void;
  /** The workspace's plan; decides which route out it is offered. */
  studioPlan: StudioPlanId;
  /** Which action the limit interrupted. */
  surface: AppLimitSurface;
  /** The app being held back, shown as the modal's card. */
  app?: AppLimitApp;
}

/** Free is sold monthly only, so its picker opens on the monthly tab. */
const DEFAULT_INTERVAL: StudioBillingInterval = 'month';

/**
 * Owns what happens after the app-limit paywall.
 *
 * The fee route charges before it builds: confirming posts the purchase and the
 * modal stays open under a loading CTA until Stripe answers, so a declined card
 * is shown here — with the action still pending and retryable — instead of the
 * app being built and installed for free while the charge fails somewhere behind
 * it. Only a settled charge calls `onProceed`.
 *
 * The upgrade route submits the picked plan through the plans page's own
 * plan-change flow (`useStudioPlanChange`), which sends a Freemium workspace to
 * Stripe checkout because it has no card on file. One upgrade implementation,
 * shared with the settings page. An upgrade that applies without checkout —
 * a card already on file — resumes the interrupted action, since the larger
 * allowance it just bought is what was blocking it.
 */
export const AppLimitGate: React.FC<AppLimitGateProps> = ({
  onClose,
  onProceed,
  studioPlan,
  surface,
  app,
}) => {
  // Set when the charge is refused, so the modal can say so and let the user try
  // again rather than closing on a failure the action depended on.
  const [hasChargeFailed, setHasChargeFailed] = useState(false);

  const [purchaseExtraApp, { isLoading: isCharging }] =
    usePurchaseExtraAppMutation();

  // One route out per plan, keyed off the plan the rest of the UI shows rather
  // than the usage read's `canAddPaidExtras`: that flag follows the Stripe
  // subscription, so a workspace on a paid plan without one would be pitched an
  // upgrade it does not need. Free is the only plan the backend hard-blocks from
  // extra apps, and the only one whose allowance a paid plan actually raises.
  const route: AppLimitRoute = studioPlan === 'freemium' ? 'upgrade' : 'fee';

  const [interval, selectInterval] =
    useState<StudioBillingInterval>(DEFAULT_INTERVAL);
  const [selectedPlan, setSelectedPlan] =
    useState<SellableStudioPlanId>(DEFAULT_UPGRADE_PLAN);

  const { submitUpgrade, isUpgrading } = useStudioPlanChange({
    onUpgradeComplete: onProceed,
  });

  // Only fetched where the upgrade is the route on offer; it prices the picker.
  const { data } = useGetStudioPlansQuery(undefined, {
    skip: route !== 'upgrade',
  });

  // Stripe's quote for one more app, read only where the fee is on offer — the
  // endpoint rejects Free outright. Normally already cached: `useAppLimit` warms
  // it on the surfaces that can open this modal, so the price paints on open
  // rather than after a proration round-trip. Still re-quoted on mount, since the
  // amount due now prorates against the time left in the period — but that runs
  // behind the cached price instead of a skeleton.
  const {
    data: feePreview,
    isError: isFeeUnquoted,
    refetch: refetchFeePreview,
  } = useGetExtraAppsPreviewQuery(undefined, {
    skip: route !== 'fee',
    refetchOnMountOrArgChange: true,
  });

  if (route === 'upgrade') {
    const options = getUpgradePlanOptions(data?.plans);
    const chosen = options.find((option) => option.planId === selectedPlan);

    return (
      <AppLimitUpgradeModal
        onClose={onClose}
        options={options}
        selectedPlan={selectedPlan}
        onSelectPlan={setSelectedPlan}
        interval={interval}
        onChangeInterval={selectInterval}
        isSubmitting={isUpgrading}
        surface={surface}
        onConfirm={() => {
          // The CTA only enables once the catalog carries the picked plan, so
          // this is the type narrowing rather than a state the user can reach.
          if (!chosen) return;
          void submitUpgrade(
            toPlanChangeSelection(chosen.planId, interval, chosen.tier),
          );
        }}
      />
    );
  }

  // Charge, then hand back. The modal is deliberately left open across the await:
  // it is the only thing telling the user their click is still in flight, and on
  // a refusal it is where the reason belongs.
  const takeFeeThenProceed = async () => {
    setHasChargeFailed(false);
    try {
      await purchaseExtraApp().unwrap();
    } catch {
      setHasChargeFailed(true);
      return;
    }
    onProceed();
  };

  return (
    <AppLimitModal
      onClose={onClose}
      isConfirming={isCharging}
      hasChargeFailed={hasChargeFailed}
      feePreview={feePreview}
      isFeeUnquoted={isFeeUnquoted}
      onRetryFee={() => {
        void refetchFeePreview();
      }}
      surface={surface}
      app={app}
      onConfirm={() => {
        void takeFeeThenProceed();
      }}
    />
  );
};
